import Box from "@/common/components/Box";
import TextField from "@/common/components/TextField";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import { zodResolver } from "@hookform/resolvers/zod";
import { Eye, EyeOff } from "lucide-react";
import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { resetPasswordSchema, ResetPasswordValidationSchema } from "../../schemas/resetPassword.schema";
import useResetPassword from "../../hooks/useResetPassword";
import { useFilterParams } from "@/common/hooks/useFilterParams";

const ResetPassword = () => {
  const navigate = useNavigate();

  const { filterParams } = useFilterParams();

  const [requestError, setRequestError] = useState(false);
  const [title, setTitle] = useState("");
  const [messages, setMessages] = useState<string[] | boolean>(false);
  const [isError, setIsError] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<ResetPasswordValidationSchema>({
    resolver: zodResolver(resetPasswordSchema)
  });

  const { mutate: resetPassword, isPending: resetPasswordIsPending } = useResetPassword({
    reset_password_token: filterParams.reset_password_token,
    onSuccess: () => {
      setRequestError(false);
      navigate('/login', { replace: true });
    },
    onError: (error) => {
      const responseData = error?.response?.data as {
          errors?: {
            base?: string[];
            reset_password_token?: string[];
          };
        };

      if (responseData?.errors?.reset_password_token?.length) {
        setTitle("Erro no Token");
        setRequestError(true);
        setIsError(true);
        setMessages(responseData.errors.reset_password_token);
      } else {
        setTitle("Erro ao resetar senha");
        setRequestError(true);
        setIsError(true);
        setMessages(responseData?.errors?.base || ['Ocorreu um erro inesperado']);
      }
    }
  });

  const onSubmit: SubmitHandler<ResetPasswordValidationSchema> = (data) => resetPassword(data);

  useEffect(() => {
    if (!filterParams.reset_password_token) {
      navigate('/login');
    }
  }, [filterParams.reset_password_token]);

  return (
    <div className="flex flex-col items-center gap-8 w-screen">
      <img src="/Logo.svg" />
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box className=" flex flex-col gap-4 rounded-xl rounded-b-none backdrop-blur-lg w-[460px] h-[280px]">
          <div className="flex flex-col gap-2">
            <h3 className="text-2xl font-bold">
              Crie nova senha
            </h3>
          </div>

          <TextField
            label="Insira a nova senha"
            type={showPassword ? 'text' : 'password'}
            RightIcon={showPassword ? <EyeOff /> : <Eye />}
            onIconClick={() => setShowPassword(!showPassword)}
            variant={errors.password?.message || requestError ? 'error' : 'default'}
            errorMessage={errors.password?.message || undefined}
            {...register('password')}
          />

          <TextField
            label="Confirme a nova senha"
            type={showConfirmPassword ? 'text' : 'password'}
            RightIcon={showConfirmPassword ? <EyeOff /> : <Eye />}
            onIconClick={() => setShowConfirmPassword(!showConfirmPassword)}
            variant={errors.confirmPassword?.message ? 'error' : 'default'}
            errorMessage={errors.confirmPassword?.message || undefined}
            {...register('confirmPassword')}
          />
        </Box>

        <button type="submit" className="w-full bg-white-800 text-black rounded-t-md py-4">
          {resetPasswordIsPending ? 'Carregando...' : 'Continuar'}
        </button>

        <button type="button" className="w-full bg-black text-white rounded-b-lg py-4" onClick={() => navigate('/login')}>
          Cancelar
        </button>
      </form>

      <div className="flex flex-col items-center">
        <span className="text-[10px] italic font-light">
          Desenvolvido por
        </span>

        <img src='/Avantsoft.svg' />
      </div>

      {messages && (
        <ModalMessageSimple
          title={title}
          messages={messages}
          setMessages={setMessages}
          isError={isError}
        />
      )}
    </div>
  );
};

export default ResetPassword;
